import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../../contexts/SupabaseContext';
import { ArrowLeft, Mail, Calendar, CreditCard, Users, User } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UserDetails {
  id: string;
  nome: string;
  email: string;
  plan: string;
  status: string;
  created_at: string;
  trial_end_date?: string;
  subscription_start_date?: string;
  subscription_end_date?: string;
  last_login?: string;
}

interface UserStats {
  totalGroups: number;
  totalPlayers: number;
  totalGames: number;
  pageViews: number;
}

const UserDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const [user, setUser] = useState<UserDetails | null>(null);
  const [stats, setStats] = useState<UserStats>({
    totalGroups: 0,
    totalPlayers: 0,
    totalGames: 0,
    pageViews: 0,
  });
  const [loading, setLoading] = useState(true);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    if (id) {
      fetchUserDetails();
      fetchUserStats();
    }
  }, [id]);

  const fetchUserDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setUser(data);
      setSelectedPlan(data.plan || 'free');
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const [groupsRes, playersRes, gamesRes, analyticsRes] = await Promise.all([
        supabase.from('groups').select('id', { count: 'exact' }).eq('creator_id', id),
        supabase.from('players').select('id', { count: 'exact' }).eq('created_by', id),
        supabase.from('games').select('id', { count: 'exact' }).eq('created_by', id),
        supabase.from('user_analytics').select('page_view', { count: 'exact' }).eq('user_id', id),
      ]);

      setStats({
        totalGroups: groupsRes.count || 0,
        totalPlayers: playersRes.count || 0,
        totalGames: gamesRes.count || 0,
        pageViews: analyticsRes.count || 0,
      });
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const handleUpdatePlan = async () => {
    if (!id || !selectedPlan) return;

    try {
      setUpdatingPlan(true);

      const updates: any = { plan: selectedPlan };

      if (selectedPlan === 'free') {
        updates.status = 'active';
        updates.subscription_start_date = null;
        updates.subscription_end_date = null;
        updates.trial_end_date = null;
      } else {
        updates.status = 'active';
        updates.subscription_start_date = new Date().toISOString();
        if (selectedPlan === 'monthly') {
          const endDate = new Date();
          endDate.setMonth(endDate.getMonth() + 1);
          updates.subscription_end_date = endDate.toISOString();
        } else if (selectedPlan === 'annual') {
          const endDate = new Date();
          endDate.setFullYear(endDate.getFullYear() + 1);
          updates.subscription_end_date = endDate.toISOString();
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      alert('Plano atualizado com sucesso!');
      fetchUserDetails();
    } catch (error) {
      console.error('Error updating plan:', error);
      alert('Erro ao atualizar plano');
    } finally {
      setUpdatingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Usuário não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <h1 className="text-2xl font-bold text-white">Detalhes do Usuário</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Informações Pessoais</h2>
            <div className="space-y-4">
              <div>
                <p className="text-slate-400 text-sm">Nome</p>
                <p className="text-white font-medium">{user.nome}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Email</p>
                <p className="text-white font-medium">{user.email}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Data de Cadastro</p>
                <p className="text-white font-medium">
                  {format(new Date(user.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </p>
              </div>
              {user.last_login && (
                <div>
                  <p className="text-slate-400 text-sm">Último Login</p>
                  <p className="text-white font-medium">
                    {format(new Date(user.last_login), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Estatísticas de Uso</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900 rounded-lg p-4">
                <Users className="w-8 h-8 text-blue-400 mb-2" />
                <p className="text-2xl font-bold text-white">{stats.totalGroups}</p>
                <p className="text-slate-400 text-sm">Grupos</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <User className="w-8 h-8 text-green-400 mb-2" />
                <p className="text-2xl font-bold text-white">{stats.totalPlayers}</p>
                <p className="text-slate-400 text-sm">Jogadores</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <Calendar className="w-8 h-8 text-purple-400 mb-2" />
                <p className="text-2xl font-bold text-white">{stats.totalGames}</p>
                <p className="text-slate-400 text-sm">Jogos</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4">
                <Mail className="w-8 h-8 text-amber-400 mb-2" />
                <p className="text-2xl font-bold text-white">{stats.pageViews}</p>
                <p className="text-slate-400 text-sm">Visualizações</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Assinatura</h2>
            <div className="space-y-4">
              <div>
                <p className="text-slate-400 text-sm mb-2">Plano Atual</p>
                <div className={`px-3 py-2 rounded-lg text-center font-medium ${
                  user.plan === 'free' ? 'bg-gray-500' :
                  user.plan === 'monthly' ? 'bg-blue-500' : 'bg-purple-500'
                } text-white`}>
                  {user.plan === 'free' ? 'Gratuito' :
                   user.plan === 'monthly' ? 'Mensal' : 'Anual'}
                </div>
              </div>

              <div>
                <p className="text-slate-400 text-sm mb-2">Status</p>
                <div className={`px-3 py-2 rounded-lg text-center font-medium ${
                  user.status === 'active' ? 'bg-green-500' :
                  user.status === 'trial' ? 'bg-yellow-500' : 'bg-red-500'
                } text-white`}>
                  {user.status === 'active' ? 'Ativo' :
                   user.status === 'trial' ? 'Trial' : 'Inativo'}
                </div>
              </div>

              {user.subscription_end_date && (
                <div>
                  <p className="text-slate-400 text-sm">Renovação</p>
                  <p className="text-white font-medium">
                    {format(new Date(user.subscription_end_date), 'dd/MM/yyyy', { locale: ptBR })}
                  </p>
                </div>
              )}

              {user.trial_end_date && (
                <div>
                  <p className="text-slate-400 text-sm">Trial até</p>
                  <p className="text-white font-medium">
                    {format(new Date(user.trial_end_date), 'dd/MM/yyyy', { locale: ptBR })}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Alterar Plano</h2>
            <div className="space-y-4">
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="free">Gratuito</option>
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
              </select>

              <button
                onClick={handleUpdatePlan}
                disabled={updatingPlan || selectedPlan === user.plan}
                className="w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
              >
                {updatingPlan ? 'Atualizando...' : 'Atualizar Plano'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsPage;
